import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, AlertTriangle, DollarSign, Link2, Package, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { allocatePayment, validateOrderForPayment } from '../../api/wholesale/payments';
import { getDistributorUnpaidOrders } from '../../api/wholesale/payments';

const AllocatePaymentModal = ({ payment, isOpen, onClose, onSuccess }) => {
  const [allocationType, setAllocationType] = useState('order_payment');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [unpaidOrders, setUnpaidOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [allocationPreview, setAllocationPreview] = useState(null);

  // Load unpaid orders for the distributor
  const loadUnpaidOrders = useCallback(async () => {
    if (!payment?.distributor_id) return;
    setLoadingOrders(true);
    try {
      const response = await getDistributorUnpaidOrders(payment.distributor_id);
      const orders = response?.data || response || [];
      setUnpaidOrders(Array.isArray(orders) ? orders : []);
    } catch (err) {
      console.error('Error loading unpaid orders:', err);
      setUnpaidOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, [payment?.distributor_id]);

  useEffect(() => {
    if (isOpen && payment) {
      setAllocationType('order_payment');
      setSelectedOrder(null);
      setValidationResult(null);
      setAllocationPreview(null);
      setOrderSearch('');
      loadUnpaidOrders();
    }
  }, [isOpen, payment, loadUnpaidOrders]);

  // When an order is selected, validate it
  const handleOrderSelect = async (order) => {
    setSelectedOrder(order);
    setValidationResult(null);
    setAllocationPreview(null);

    try {
      const result = await validateOrderForPayment(order.id);
      const validation = result?.data;
      setValidationResult(validation);

      if (validation?.can_accept) {
        const paymentAmount = parseFloat(payment.payment_amount) || 0;
        const balanceDue = validation.balance_due;
        const excess = Math.max(0, paymentAmount - balanceDue);
        const amountApplied = Math.min(paymentAmount, balanceDue);

        setAllocationPreview({
          order_number: order.order_number,
          balance_due: balanceDue,
          payment_amount: paymentAmount,
          amount_applied: amountApplied,
          excess_amount: excess,
          new_outstanding: Math.max(0, balanceDue - amountApplied),
          fully_paid: amountApplied >= balanceDue
        });
      }
    } catch (err) {
      console.error('Error validating order:', err);
      setValidationResult({ can_accept: false, reason: 'Failed to validate order' });
    }
  };

  const handleSubmit = async () => {
    if (allocationType === 'order_payment' && !selectedOrder) {
      toast.error('Please select an order to link this payment to');
      return;
    }

    setSubmitting(true);
    try {
      const result = await allocatePayment(payment.id, {
        allocation_type: allocationType,
        order_id: allocationType === 'order_payment' ? selectedOrder.id : undefined
      });

      const message =
        result?.allocation_details?.message || result?.message || 'Payment allocated successfully';
      toast.success(message, { autoClose: 8000 });
      onSuccess?.();
    } catch (err) {
      toast.error(err.message || 'Failed to allocate payment');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter orders by search
  const filteredOrders = unpaidOrders.filter((order) => {
    if (!orderSearch) return true;
    const q = orderSearch.toLowerCase();
    return (
      (order.order_number || '').toLowerCase().includes(q) ||
      (order.distributor_name || '').toLowerCase().includes(q) ||
      String(order.total_amount || '').includes(q)
    );
  });

  if (!isOpen || !payment) return null;

  const paymentAmount = parseFloat(payment.payment_amount) || 0;
  const distributorName =
    payment.distributor_business_name ||
    payment.business_name ||
    payment.distributor_name ||
    'Unknown';

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-red-50 rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-red-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Allocate Unlinked Payment
            </h2>
            <p className="text-sm text-red-600 mt-1">
              This payment is not linked to any order or account balance
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Payment Info */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-500">Payment #</p>
              <p className="text-sm font-bold text-gray-900">{payment.payment_number}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Amount</p>
              <p className="text-lg font-bold text-emerald-600">
                ₦{paymentAmount.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Distributor</p>
              <p className="text-sm font-medium text-gray-900">{distributorName}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Allocation Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Where should this payment go?
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setAllocationType('order_payment');
                  setAllocationPreview(null);
                }}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                  allocationType === 'order_payment'
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Package
                  className={`w-6 h-6 ${allocationType === 'order_payment' ? 'text-blue-600' : 'text-gray-400'}`}
                />
                <div className="text-left">
                  <p
                    className={`font-semibold ${allocationType === 'order_payment' ? 'text-blue-900' : 'text-gray-700'}`}
                  >
                    Link to Order
                  </p>
                  <p className="text-xs text-gray-500">Apply to a specific order</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => {
                  setAllocationType('account_balance');
                  setSelectedOrder(null);
                  setAllocationPreview(null);
                  setValidationResult(null);
                }}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                  allocationType === 'account_balance'
                    ? 'border-purple-500 bg-purple-50 shadow-md'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <DollarSign
                  className={`w-6 h-6 ${allocationType === 'account_balance' ? 'text-purple-600' : 'text-gray-400'}`}
                />
                <div className="text-left">
                  <p
                    className={`font-semibold ${allocationType === 'account_balance' ? 'text-purple-900' : 'text-gray-700'}`}
                  >
                    Account Balance
                  </p>
                  <p className="text-xs text-gray-500">Add to customer's balance</p>
                </div>
              </button>
            </div>
          </div>

          {/* Order Selection (only show when order_payment) */}
          {allocationType === 'order_payment' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Select Order</label>

              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  placeholder="Search orders..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Orders List */}
              <div className="border border-gray-200 rounded-lg max-h-52 overflow-y-auto">
                {loadingOrders ? (
                  <div className="p-4 text-center text-sm text-gray-500">Loading orders...</div>
                ) : filteredOrders.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    No unpaid orders found
                  </div>
                ) : (
                  filteredOrders.map((order) => {
                    const balanceDue = parseFloat(order.balance_due || order.total_amount) || 0;
                    const isSelected = selectedOrder?.id === order.id;

                    return (
                      <button
                        key={order.id}
                        onClick={() => handleOrderSelect(order)}
                        className={`w-full flex items-center justify-between px-4 py-3 text-left border-b last:border-b-0 transition-colors ${
                          isSelected
                            ? 'bg-blue-50 border-l-4 border-l-blue-500'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {order.order_number}
                            {isSelected && (
                              <CheckCircle className="w-4 h-4 text-blue-600 inline ml-2" />
                            )}
                          </p>
                          <p className="text-xs text-gray-500">
                            {order.order_date
                              ? new Date(order.order_date).toLocaleDateString()
                              : ''}{' '}
                            • Total: ₦{parseFloat(order.total_amount || 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-red-600">
                            ₦{balanceDue.toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-400">Outstanding</p>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {/* Validation Error */}
              {validationResult && !validationResult.can_accept && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {validationResult.reason}
                  </p>
                </div>
              )}

              {/* Allocation Preview */}
              {allocationPreview && validationResult?.can_accept && (
                <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                  <p className="text-sm font-semibold text-blue-800">Allocation Preview</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500">Order Outstanding</p>
                      <p className="font-medium text-gray-900">
                        ₦{allocationPreview.balance_due.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Payment Amount</p>
                      <p className="font-medium text-gray-900">
                        ₦{allocationPreview.payment_amount.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Applied to Order</p>
                      <p className="font-bold text-emerald-600">
                        ₦{allocationPreview.amount_applied.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">New Outstanding</p>
                      <p
                        className={`font-bold ${allocationPreview.new_outstanding === 0 ? 'text-emerald-600' : 'text-amber-600'}`}
                      >
                        ₦{allocationPreview.new_outstanding.toLocaleString()}
                        {allocationPreview.fully_paid && (
                          <span className="ml-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                            FULLY PAID
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  {allocationPreview.excess_amount > 0 && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-700">
                        <strong>₦{allocationPreview.excess_amount.toLocaleString()}</strong> excess
                        will be added to <strong>{distributorName}</strong>'s account balance upon
                        verification.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Account Balance Preview */}
          {allocationType === 'account_balance' && (
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-purple-800">
                <strong>₦{paymentAmount.toLocaleString()}</strong> will be added to{' '}
                <strong>{distributorName}</strong>'s account balance upon verification.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              submitting ||
              (allocationType === 'order_payment' &&
                (!selectedOrder || !validationResult?.can_accept))
            }
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Allocating...
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4" />
                Allocate Payment
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AllocatePaymentModal;
